import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  addYears,
  isAfter,
  isBefore,
} from 'date-fns';
import { z } from 'zod';
import {
  storageAllocationMethodValues,
  storageCapacityCategoryValues,
  storageCommodityUnitValues,
  storageFlowUnitValues,
  storageProductPeriodValues,
  storageProductTypeValues,
} from 'src/apis/storage/mappings';
import type {
  StorageCommodityUnit,
  StorageDownloadFileWrite,
  StorageFlowUnit,
  StorageOfferWriteData,
  StorageProductPeriod,
  StorageSbuUnit,
} from 'src/apis/storage/types';
import { searchableSingleSelectSchema } from 'src/components/form/select/searchable-single-select';
import {
  preprocessAsArray,
  requiredOutput,
} from 'src/components/form/zod-utilities';
import type { Currency } from 'src/utils/currencies';

const STORAGE_OFFER_CURRENCY: Currency = 'EUR';

const optionalAmountSchema = z
  .number()
  .min(0, '{label} must be greater than or equal to 0.')
  .nullable()
  .optional();

const attachedFileSchema = z.object({
  file: z
    .object({
      id: z.string(),
      fileName: z.string(),
    })
    .nullable(),
});

const productComponentSchema = ({
  productRuntimeStart,
  productRuntimeEnd,
}: {
  productRuntimeStart: string | null;
  productRuntimeEnd: string | null;
}) =>
  z
    .object({
      id: z.string(),
      capacityCategory: z
        .enum(storageCapacityCategoryValues)
        .nullable()
        .transform(requiredOutput()),
      runtimeStart: z
        .string()
        .refine(
          (value) =>
            productRuntimeStart
              ? !isBefore(new Date(value), new Date(productRuntimeStart))
              : true,
          'Please enter a {label} date that is after or equal to the Product Runtime Start date.'
        )
        .refine(
          (value) =>
            productRuntimeEnd
              ? isBefore(new Date(value), new Date(productRuntimeEnd))
              : true,
          'Please enter a {label} date that is before the Product Runtime End date.'
        )
        .nullable()
        .transform(
          requiredOutput({ message: 'Please enter a value for {label}.' })
        ),
      runtimeEnd: z
        .string()
        .refine(
          (value) =>
            productRuntimeEnd
              ? !isAfter(new Date(value), new Date(productRuntimeEnd))
              : true,
          'Please enter a {label} date that is before or equal to the Product Runtime End date.'
        )
        .nullable()
        .transform(
          requiredOutput({ message: 'Please enter a value for {label}.' })
        ),
      storageVolumeAmount: optionalAmountSchema,
      storageVolumeUnit: z
        .enum(storageCommodityUnitValues)
        .nullable()
        .optional(),
      injectionCapacityAmount: optionalAmountSchema,
      injectionCapacityUnit: z
        .enum(storageFlowUnitValues)
        .nullable()
        .optional(),
      withdrawalCapacityAmount: optionalAmountSchema,
      withdrawalCapacityUnit: z
        .enum(storageFlowUnitValues)
        .nullable()
        .optional(),
    })
    .superRefine((comp, ctx) => {
      if (
        comp.runtimeStart &&
        comp.runtimeEnd &&
        !isAfter(new Date(comp.runtimeEnd), new Date(comp.runtimeStart))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Please enter a {label} date that is after the Runtime Start date.',
          path: ['runtimeEnd'],
        });
      }
    });

export type ProductComponentSchema = z.input<
  ReturnType<typeof productComponentSchema>
>;

export const getStorageOfferSchema = ({
  now,
  publication,
  biddingWindowStart,
  biddingWindowEnd,
  productRuntimeStart,
  productRuntimeEnd,
  productPeriod,
}: {
  now: number;
  publication: string | null;
  biddingWindowStart: string | null;
  biddingWindowEnd: string | null;
  productRuntimeStart: string | null;
  productRuntimeEnd: string | null;
  productPeriod: StorageProductPeriod | null;
}) => {
  return z.object({
    name: z
      .string()
      .max(255, 'Please enter a {label} that is shorter than 255 characters.')
      .nullable()
      .transform(requiredOutput()),

    storageLocationId: searchableSingleSelectSchema,

    productPeriod: z
      .enum(storageProductPeriodValues)
      .nullable()
      .transform(requiredOutput()),

    publication: z
      .string()
      .refine(
        (value) => isAfter(new Date(value), now),
        'Please enter a {label} date that is in the future.'
      )
      .nullable()
      .transform(
        requiredOutput({ message: 'Please enter a value for {label}.' })
      ),

    biddingWindowStart: z
      .string()
      .refine(
        (value) =>
          publication ? isAfter(new Date(value), new Date(publication)) : true,
        'Please enter a {label} date that is after the Offer Publication date.'
      )
      .nullable()
      .transform(
        requiredOutput({ message: 'Please enter a value for {label}.' })
      ),

    biddingWindowEnd: z
      .string()
      .refine(
        (value) =>
          biddingWindowStart
            ? isAfter(new Date(value), new Date(biddingWindowStart))
            : true,
        'Please enter a {label} date that is after the Bidding Window Start date.'
      )
      .nullable()
      .transform(
        requiredOutput({ message: 'Please enter a value for {label}.' })
      ),

    productRuntimeStart: z
      .string()
      .refine(
        (value) =>
          publication ? isAfter(new Date(value), new Date(publication)) : true,
        'Please enter a {label} date that is after the Offer Publication date.'
      )
      .refine(
        (value) =>
          biddingWindowEnd
            ? isAfter(new Date(value), new Date(biddingWindowEnd))
            : true,
        'Please enter a {label} date that is after the Bidding Window End date.'
      )
      .nullable()
      .transform(
        requiredOutput({ message: 'Please enter a value for {label}.' })
      ),

    productRuntimeEnd: z
      .string()
      .refine(
        (value) =>
          productRuntimeStart
            ? isAfter(new Date(value), new Date(productRuntimeStart))
            : true,
        'Please enter a {label} date that is after the Product Runtime Start date.'
      )
      .refine((value) => {
        if (
          !productRuntimeStart ||
          !productPeriod ||
          productPeriod === 'CUSTOM'
        )
          return true;
        const start = new Date(productRuntimeStart);
        const end = new Date(value);

        if (productPeriod === 'MULTIYEAR') {
          return isAfter(end, addYears(start, 1));
        }

        const expectedEnd = {
          WITHINDAY: addHours(start, 24),
          DAY: addDays(start, 1),
          WEEK: addWeeks(start, 1),
          MONTH: addMonths(start, 1),
          YEAR: addYears(start, 1),
        }[productPeriod];

        return end.getTime() === expectedEnd.getTime();
      }, `Product Runtime must span exactly 1 ${productPeriod?.toLowerCase()} for this period. Please adjust the runtime or select "Custom" as the Product Period.`)
      .nullable()
      .transform(
        requiredOutput({ message: 'Please enter a value for {label}.' })
      ),

    productType: z.enum(storageProductTypeValues),

    typeOfProductUnit: z.string().nullable().transform(requiredOutput()),

    allocationMethod: z
      .enum(storageAllocationMethodValues)
      .nullable()
      .transform(requiredOutput()),

    productName: z
      .string()
      .max(255, 'Please enter a {label} that is shorter than 255 characters.')
      .nullable()
      .transform(requiredOutput()),

    availableQuantity: z
      .number()
      .int('{label} cannot contain decimals.')
      .min(1, '{label} must be greater than or equal to 1.')
      .nullable()
      .transform(requiredOutput()),

    reservePrice: z
      .number()
      .min(0, '{label} must be greater than or equal to 0.')
      .nullable()
      .optional(),

    productComponents: z.preprocess(
      preprocessAsArray,
      z
        .array(
          productComponentSchema({ productRuntimeStart, productRuntimeEnd })
        )
        .superRefine((components, ctx) => {
          components.forEach((comp, index) => {
            if (
              (comp.storageVolumeAmount != null) !==
              (comp.storageVolumeUnit != null)
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  comp.storageVolumeUnit == null
                    ? 'Please select a {label}.'
                    : 'Please enter a {label}.',
                path: [
                  index,
                  comp.storageVolumeUnit == null
                    ? 'storageVolumeUnit'
                    : 'storageVolumeAmount',
                ],
              });
            }
            if (
              (comp.injectionCapacityAmount != null) !==
              (comp.injectionCapacityUnit != null)
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  comp.injectionCapacityUnit == null
                    ? 'Please select a {label}.'
                    : 'Please enter a {label}.',
                path: [
                  index,
                  comp.injectionCapacityUnit == null
                    ? 'injectionCapacityUnit'
                    : 'injectionCapacityAmount',
                ],
              });
            }
            if (
              (comp.withdrawalCapacityAmount != null) !==
              (comp.withdrawalCapacityUnit != null)
            ) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  comp.withdrawalCapacityUnit == null
                    ? 'Please select a {label}.'
                    : 'Please enter a {label}.',
                path: [
                  index,
                  comp.withdrawalCapacityUnit == null
                    ? 'withdrawalCapacityUnit'
                    : 'withdrawalCapacityAmount',
                ],
              });
            }
          });
        })
    ),
    offerSpecificNote: z
      .string()
      .nullable()
      .optional()
      .transform((v) => (v?.trim() ? v : null)),

    attachedFiles: z.array(attachedFileSchema).default([]),
  });
};

type StorageOfferSchema = ReturnType<typeof getStorageOfferSchema>;

export type StorageOfferFormValues = z.input<StorageOfferSchema>;

export type ValidStorageOfferFormValues = z.output<StorageOfferSchema>;

function buildTypeOfProduct(
  values: ValidStorageOfferFormValues
): StorageOfferWriteData['typeOfProduct'] {
  const { productType, typeOfProductUnit } = values;
  switch (productType) {
    case 'storageVolume':
      return {
        typeOfProduct: 'STORAGE_VOLUME',
        unit: typeOfProductUnit as StorageCommodityUnit,
      };
    case 'injectionCapacity':
      return {
        typeOfProduct: 'INJECTION_CAPACITY',
        unit: typeOfProductUnit as StorageFlowUnit,
      };
    case 'withdrawalCapacity':
      return {
        typeOfProduct: 'WITHDRAWAL_CAPACITY',
        unit: typeOfProductUnit as StorageFlowUnit,
      };
    case 'SBU':
      return {
        typeOfProduct: 'SBU',
        unit: typeOfProductUnit as StorageSbuUnit,
      };
  }
}

function mapProductComponent(
  comp: ValidStorageOfferFormValues['productComponents'][number]
) {
  const runtime = { start: comp.runtimeStart, end: comp.runtimeEnd };
  const storageVolume =
    comp.storageVolumeAmount != null &&
    comp.storageVolumeAmount >= 0 &&
    comp.storageVolumeUnit
      ? { amount: comp.storageVolumeAmount, unit: comp.storageVolumeUnit }
      : null;
  const injectionCapacity =
    comp.injectionCapacityAmount != null &&
    comp.injectionCapacityAmount >= 0 &&
    comp.injectionCapacityUnit
      ? {
          amount: comp.injectionCapacityAmount,
          unit: comp.injectionCapacityUnit,
        }
      : null;
  const withdrawalCapacity =
    comp.withdrawalCapacityAmount != null &&
    comp.withdrawalCapacityAmount >= 0 &&
    comp.withdrawalCapacityUnit
      ? {
          amount: comp.withdrawalCapacityAmount,
          unit: comp.withdrawalCapacityUnit,
        }
      : null;
  return {
    capacityCategory: comp.capacityCategory,
    runtime,
    storageVolume: storageVolume
      ? { amount: storageVolume.amount, unit: storageVolume.unit }
      : null,
    injectionCapacity: injectionCapacity
      ? { amount: injectionCapacity.amount, unit: injectionCapacity.unit }
      : null,
    withdrawalCapacity: withdrawalCapacity
      ? { amount: withdrawalCapacity.amount, unit: withdrawalCapacity.unit }
      : null,
  };
}

export function formValuesToWriteData(
  values: ValidStorageOfferFormValues
): StorageOfferWriteData {
  const reservePrice =
    values.reservePrice != null
      ? { amount: values.reservePrice, currency: STORAGE_OFFER_CURRENCY }
      : null;

  const attachedFiles: StorageDownloadFileWrite[] = Array.isArray(
    values.attachedFiles
  )
    ? values.attachedFiles
        .filter(
          (item): item is { file: { id: string; fileName: string } } =>
            item.file != null &&
            typeof item.file.id === 'string' &&
            typeof item.file.fileName === 'string'
        )
        .map((item) => ({
          id: item.file.id,
          fileName: item.file.fileName,
        }))
    : [];

  return {
    name: values.name,
    storageLocationId: values.storageLocationId.value,
    productRuntime: {
      start: values.productRuntimeStart,
      end: values.productRuntimeEnd,
    },
    publication: values.publication,
    biddingWindow: {
      start: values.biddingWindowStart,
      end: values.biddingWindowEnd,
    },
    typeOfProduct: buildTypeOfProduct(values),
    allocationMethod: values.allocationMethod,
    productName: values.productName,
    availableQuantity: values.availableQuantity,
    reservePrice,
    productComponents:
      values.productComponents.length > 0
        ? values.productComponents.map(mapProductComponent)
        : [],
    offerSpecificNote: values.offerSpecificNote,
    productPeriod: values.productPeriod,
    attachedFiles,
  };
}
